import { useCallback, useRef, useState } from 'react';
import {
  hasTrainingTranslationGaps,
  type TranslationTraining
} from './translationGaps';

type PublishTrainingParams = {
  should_auto_update_translations: boolean;
};

type PublishTrainingResponse = {
  redirectUrl?: string;
};

type CourseInfoResponse = {
  error?: boolean;
  training?: TranslationTraining;
};

type CourseEditorApi = {
  publishTraining: (
    trainingId: number,
    params: PublishTrainingParams
  ) => Promise<PublishTrainingResponse>;
  getCourseInfo: (trainingId: number) => Promise<CourseInfoResponse>;
};

type UseExitPublishFlowParams = {
  apiService: CourseEditorApi | null;
  training: TranslationTraining | null | undefined;
  publishRedirectUrl?: string | null;
  exitRedirectUrl: string;
  shouldCheckTranslationsBeforeExit: boolean;
  hasTranslationGaps: boolean;
  setTraining: (training: TranslationTraining) => void;
  setDisplayUpdateTranslationsModal: (display: boolean) => void;
  setShouldAutoUpdateTranslations: (
    shouldAutoUpdateTranslations: boolean
  ) => void;
  onError: () => void;
};

type UseExitPublishFlowResult = {
  isExitInProgress: boolean;
  doPublish: (shouldAutoUpdateTranslations: boolean) => Promise<boolean>;
  publishFromModal: (shouldAutoUpdateTranslations: boolean) => Promise<void>;
  onExit: () => Promise<void>;
};

export const useExitPublishFlow = ({
  apiService,
  training,
  publishRedirectUrl,
  exitRedirectUrl,
  shouldCheckTranslationsBeforeExit,
  hasTranslationGaps,
  setTraining,
  setDisplayUpdateTranslationsModal,
  setShouldAutoUpdateTranslations,
  onError
}: UseExitPublishFlowParams): UseExitPublishFlowResult => {
  const [isExitInProgress, setIsExitInProgress] = useState(false);
  const exitInProgressRef = useRef(false);

  const doPublish = useCallback(
    async (shouldAutoUpdateTranslations: boolean): Promise<boolean> => {
      if (!apiService || training?.id == null) return false;

      const params = {
        should_auto_update_translations: shouldAutoUpdateTranslations
      };

      const publishResponse = await apiService.publishTraining(
        training.id,
        params
      );

      if (publishRedirectUrl) {
        const resultRedirectUrl =
          process.env.REACT_APP_BACKEND_DASHBOARD_BASE_URL + publishRedirectUrl;

        if (window.top && window.self !== window.top) {
          window.top.location.href = resultRedirectUrl;
        } else {
          window.location.href = resultRedirectUrl;
        }
        return true;
      }

      if (publishResponse?.redirectUrl && window) {
        if (window.top && window.self !== window.top) {
          window.top.location.href = publishResponse.redirectUrl;
        } else {
          window.location.href = publishResponse.redirectUrl;
        }
        return true;
      }

      return false;
    },
    [apiService, publishRedirectUrl, training]
  );

  const publishFromModal = useCallback(
    async (shouldAutoUpdateTranslations: boolean) => {
      if (exitInProgressRef.current) return;

      exitInProgressRef.current = true;
      setIsExitInProgress(true);

      let shouldResetExitInProgress = true;

      try {
        setShouldAutoUpdateTranslations(shouldAutoUpdateTranslations);

        const didNavigate = await doPublish(shouldAutoUpdateTranslations);
        if (!didNavigate) {
          onError();
          return;
        }

        setDisplayUpdateTranslationsModal(false);
        shouldResetExitInProgress = false;
      } catch {
        onError();
      } finally {
        if (shouldResetExitInProgress) {
          exitInProgressRef.current = false;
          setIsExitInProgress(false);
        }
      }
    },
    [
      doPublish,
      onError,
      setDisplayUpdateTranslationsModal,
      setShouldAutoUpdateTranslations
    ]
  );

  const onExit = useCallback(async () => {
    if (exitInProgressRef.current) return;

    exitInProgressRef.current = true;
    setIsExitInProgress(true);

    let shouldResetExitInProgress = true;

    try {
      if (shouldCheckTranslationsBeforeExit) {
        if (hasTranslationGaps) {
          setShouldAutoUpdateTranslations(false);
          setDisplayUpdateTranslationsModal(true);
          return;
        }

        if (!apiService || training?.id == null) {
          onError();
          return;
        }

        const latestCourse = await apiService.getCourseInfo(training.id);
        if (latestCourse?.error || !latestCourse?.training) {
          onError();
          return;
        }

        const latestHasTranslationGaps = hasTrainingTranslationGaps(
          latestCourse.training
        );

        if (latestHasTranslationGaps) {
          setTraining(latestCourse.training);
          setShouldAutoUpdateTranslations(false);
          setDisplayUpdateTranslationsModal(true);
          return;
        }
      }

      if (training?.status === 'live') {
        const didNavigate = await doPublish(false);
        if (!didNavigate) {
          onError();
          return;
        }

        shouldResetExitInProgress = false;
        return;
      }

      if (!window || !exitRedirectUrl) {
        return;
      }

      const resultRedirectUrl =
        process.env.REACT_APP_BACKEND_DASHBOARD_BASE_URL + exitRedirectUrl;

      if (window.parent && window.self !== window.parent) {
        window.parent.location.href = resultRedirectUrl;
      } else {
        window.location.href = resultRedirectUrl;
      }

      shouldResetExitInProgress = false;
    } catch {
      onError();
    } finally {
      if (shouldResetExitInProgress) {
        exitInProgressRef.current = false;
        setIsExitInProgress(false);
      }
    }
  }, [
    apiService,
    doPublish,
    exitRedirectUrl,
    hasTranslationGaps,
    onError,
    setDisplayUpdateTranslationsModal,
    setShouldAutoUpdateTranslations,
    setTraining,
    shouldCheckTranslationsBeforeExit,
    training
  ]);

  return {
    isExitInProgress,
    doPublish,
    publishFromModal,
    onExit
  };
};
